/**
 * GuidedTourProvider — tiny state machine for a step-based narrated
 * tour.  Keeps the mode (idle / auto / manual / paused), current step
 * index, and the HTMLAudioElement that plays the pre-rendered
 * ElevenLabs narration for the active step.  Falls back to browser
 * speechSynthesis only when a step has no `audio` URL (dev fallback).
 */
import { takeChannel } from "@/lib/audioPool";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const Ctx = createContext(null);

// Pacing fallback when a step has no pre-rendered audio.
const WORDS_PER_SECOND = 2.5;
const MIN_STEP_MS = 4000;
const EXTRA_PAD_MS = 1000; // breathing room after audio ends before auto-advance

export function GuidedTourProvider({ script, onComplete, getLeadInMs, children }) {
  const [mode, setMode]  = useState("idle"); // idle | auto | manual | paused
  const [step, setStep]  = useState(0);
  // Optional per-step delay before narration starts (e.g. while a beat fades in).
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const leadInRef = useRef(getLeadInMs);
  leadInRef.current = getLeadInMs;
  const [speaking, setSpeaking] = useState(false);
  const [replayNonce, setReplayNonce] = useState(0);
  const autoTimerRef = useRef(null);
  const audioRef = useRef(null);
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const blockedRef = useRef(null);
  // First tap after a blocked play: resume where we stopped.
  useEffect(() => {
    const onUnlock = () => {
      if (!blockedRef.current) return;
      const m = blockedRef.current === "manual" ? "manual" : "auto";
      blockedRef.current = null;
      setMode(m);
      setReplayNonce((n) => n + 1);
    };
    window.addEventListener("nex-audio-unlocked", onUnlock);
    return () => window.removeEventListener("nex-audio-unlocked", onUnlock);
  }, []);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const finish = useCallback(() => { setTimeout(() => onCompleteRef.current?.(), 0); }, []);

  const clearTimer = () => {
    if (autoTimerRef.current) {
      clearTimeout(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  };

  const stopSpeech = useCallback(() => {
    try {
      if (audioRef.current) {
        const old = audioRef.current;
        old.onended = null;
        old.onerror = null;
        old.onloadedmetadata = null;
        old.pause();
        old.currentTime = 0;
        audioRef.current = null;
      }
    } catch {}
    try { window.speechSynthesis?.cancel(); } catch {}
    setSpeaking(false);
  }, []);

  // Play the step audio.  Returns a Promise that resolves with the
  // approximate step duration in ms (so Auto mode can schedule the
  // next advance even if `ended` doesn't fire — e.g. autoplay policy).
  const playStep = useCallback((current, { onEnded, onDuration } = {}) => {
    stopSpeech();
    if (!current) return 0;
    if (current.silent) return MIN_STEP_MS;

    if (current.audio) {
      // Real ElevenLabs clip.
      const h = takeChannel("voice", current.audio);
      const a = h.el;
      audioRef.current = a;
      a.onended = () => { setSpeaking(false); onEnded?.(); };
      a.onerror = () => { setSpeaking(false); onEnded?.(); };
      a.onloadedmetadata = () => { if (audioRef.current === a && Number.isFinite(a.duration)) onDuration?.(a.duration * 1000); };
      setSpeaking(true);
      h.play().catch((e) => {
        if (audioRef.current !== a) return;
        setSpeaking(false);
        // Blocked by the browser (no tap yet): pause instead of racing through every beat.
        if (e?.name === "NotAllowedError") { blockedRef.current = modeRef.current; setMode("paused"); return; }
        onEnded?.();
      });
      // Rough fallback duration — audio reports real duration via
      // onloadedmetadata, so we re-schedule inside the effect below.
      const words = (current.caption || "").split(/\s+/).filter(Boolean).length;
      return Math.max(MIN_STEP_MS, (words / WORDS_PER_SECOND) * 1000);
    }

    // Dev fallback — browser TTS.
    try {
      if (!window.speechSynthesis) { onEnded?.(); return 0; }
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(current.caption);
      u.rate = 1.0; u.pitch = 1.0; u.volume = 1.0;
      u.onend = () => { setSpeaking(false); onEnded?.(); };
      u.onerror = () => { setSpeaking(false); onEnded?.(); };
      setSpeaking(true);
      window.speechSynthesis.speak(u);
      const words = current.caption.split(/\s+/).filter(Boolean).length;
      return Math.max(MIN_STEP_MS, (words / WORDS_PER_SECOND) * 1000);
    } catch { onEnded?.(); return 0; }
  }, [stopSpeech]);

  const start = useCallback((nextMode, startIndex = 0) => {
    clearTimer();
    stopSpeech();
    setStep(startIndex);
    setMode(nextMode);
  }, [stopSpeech]);

  const stop = useCallback(() => {
    clearTimer();
    setPending(false);
    stopSpeech();
    setStep(0);
    setMode("idle");
  }, [stopSpeech]);

  const next = useCallback(() => {
    clearTimer();
    stopSpeech();
    if (step + 1 >= script.length) {
      setMode("idle");
      setStep(0);
      finish();
      return;
    }
    setStep(step + 1);
  }, [script.length, stopSpeech, step, finish]);

  const prev = useCallback(() => {
    clearTimer();
    stopSpeech();
    setStep((s) => Math.max(0, s - 1));
  }, [stopSpeech]);

  const togglePause = useCallback(() => {
    setMode((m) => {
      if (m === "auto") {
        clearTimer();
        try { audioRef.current?.pause(); } catch {}
        try { window.speechSynthesis?.pause?.(); } catch {}
        setSpeaking(false);
        return "paused";
      }
      if (m === "paused") {
        try { audioRef.current?.play(); } catch {}
        try { window.speechSynthesis?.resume?.(); } catch {}
        return "auto";
      }
      return m;
    });
  }, []);

  const pauseForEdit = useCallback(() => {
    clearTimer();
    stopSpeech();
    setMode((m) => (m === "auto" ? "paused" : m));
  }, [stopSpeech]);

  const replay = useCallback(() => {
    setMode((m) => (m === "paused" ? "auto" : m));
    setReplayNonce((n) => n + 1);
  }, []);

  // Whenever the step index or mode changes, play the current step's
  // audio and, in auto mode, schedule the next advance when it ends.
  useEffect(() => {
    if (mode === "idle" || mode === "paused") return;
    const current = script[step];
    if (!current) return;

    const advance = () => {
      if (mode !== "auto") return;
      if (step + 1 >= script.length) {
        setMode("idle");
        setStep(0);
        finish();
        return;
      }
      setStep(step + 1);
    };

    const run = () => {
    pendingRef.current = false;
    setPending(false);
    const fallbackMs = playStep(current, {
      onEnded: () => {
        if (mode === "auto") {
          clearTimer();
          autoTimerRef.current = setTimeout(advance, EXTRA_PAD_MS);
        }
      },
      // Real clip length known: push the safety net past the end of the audio.
      onDuration: (ms) => {
        if (mode === "auto") {
          clearTimer();
          autoTimerRef.current = setTimeout(advance, ms + EXTRA_PAD_MS + 2000);
        }
      },
    });

    // Safety net — even if `ended` doesn't fire (browser autoplay
    // block, failed clip load), still auto-advance after a reasonable
    // guess so the tour never hangs on a dead step.
    if (mode === "auto" && fallbackMs > 0) {
      clearTimer();
      autoTimerRef.current = setTimeout(advance, fallbackMs + EXTRA_PAD_MS + 2000);
    }
    };

    const leadMs = leadInRef.current?.(step) || 0;
    let leadTimer = null;
    if (leadMs > 0) {
      stopSpeech();
      pendingRef.current = true;
      setPending(true);
      leadTimer = setTimeout(run, leadMs);
    } else run();

    return () => {
      clearTimeout(leadTimer);
      pendingRef.current = false;
      setPending(false);
      clearTimer();
    };
  }, [step, mode, script, playStep, replayNonce, finish, stopSpeech]);

  // Cancel any in-flight speech if the component unmounts.
  useEffect(() => () => stopSpeech(), [stopSpeech]);

  // Keyboard shortcuts while a tour is active.
  useEffect(() => {
    if (mode === "idle") return;
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "TEXTAREA" || tag === "INPUT") return;
      if (e.key === "Escape") stop();
      else if (e.key === "ArrowRight" || e.key === " " || e.key === "Enter") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "p" || e.key === "P") togglePause();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, stop, next, prev, togglePause]);

  // 0..1 progress through the current clip (null when no audio is loaded).
  const getAudioProgress = useCallback(() => {
    if (pendingRef.current) return 0;
    const a = audioRef.current;
    return a && Number.isFinite(a.duration) && a.duration > 0 ? a.currentTime / a.duration : null;
  }, []);

  const value = useMemo(() => ({
    script,
    getAudioProgress,
    step,
    mode,
    speaking,
    pending,
    current: script[step],
    isActive: mode !== "idle",
    isPaused: mode === "paused",
    isAuto: mode === "auto" || mode === "paused",
    start, stop, next, prev, togglePause, pauseForEdit, replay,
  }), [script, step, mode, speaking, pending, start, stop, next, prev, togglePause, pauseForEdit, replay, getAudioProgress]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useGuidedTour = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useGuidedTour must be used inside <GuidedTourProvider>");
  return ctx;
};
